import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";

export default function AcknowledgedMessagesHistory() {
    const { fetchWithAuth, isLoggedIn } = useContext(AuthContext);

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 5;

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to page 1 when search changes
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    // Reset to page 1 whenever date filters change
    useEffect(() => {
        setPage(1);
    }, [startDate, endDate]);

    useEffect(() => {
        if (!isLoggedIn) {
            setMessages([]);
            setLoading(false);
            setError("You are logged out. Please log in to see messages.");
            return;
        }

        const fetchMessages = async () => {
            setLoading(true);
            setError("");
            try {
                const params = new URLSearchParams();
                params.append("page", page);
                params.append("per_page", limit);
                if (startDate) params.append("start_date", startDate);
                if (endDate) params.append("end_date", endDate);
                if (debouncedSearch) params.append("search", debouncedSearch);

                const data = await fetchWithAuth(
                    `/api/messages/acknowledged?${params.toString()}`
                );

                if (!data || !data.data) {
                    setMessages([]);
                    setError("Unable to fetch messages. Please try again.");
                    return;
                }

                setMessages(data.data);
                setTotalPages(data.last_page || 1);
            } catch (err) {
                if (err.message.includes("<!DOCTYPE")) {
                    setMessages([]);
                    setError("You are logged out. Please log in again.");
                } else if (err.message.toLowerCase().includes("unauthorized")) {
                    setMessages([]);
                    setError("Session expired. Please log in again.");
                } else {
                    setError(err.message || "An error occurred.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [page, startDate, endDate, debouncedSearch, fetchWithAuth, isLoggedIn]);

    const handlePrev = () => page > 1 && setPage((prev) => prev - 1);
    const handleNext = () => page < totalPages && setPage((prev) => prev + 1);

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4">Your Messages</h1>

            {/* Filters */}
            <div className="flex gap-4 mb-4 flex-wrap">
                <div>
                    <label className="block mb-1 font-semibold">Start Date</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border rounded px-2 py-1"
                    />
                </div>
                <div>
                    <label className="block mb-1 font-semibold">End Date</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border rounded px-2 py-1"
                    />
                </div>
                <div>
                    <label className="block mb-1 font-semibold">Search</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by title or content"
                        className="border rounded px-2 py-1"
                    />
                </div>
            </div>

            {loading ? (
                <p>Loading messages...</p>
            ) : error ? (
                <p className="text-red-500">{error}</p>
            ) : messages.length === 0 ? (
                <p>No messages found.</p>
            ) : (
                <>
                    <ul className="space-y-4">
                        {messages.map((msg) => (
                            <li
                                key={msg.id}
                                className="border rounded p-4 shadow-sm bg-white"
                                role="listitem"
                            >
                                <p className="font-semibold">{msg.title}</p>
                                <p className="text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                                <p className="text-sm text-gray-500">
                                    Sent: {msg.action_date || "Date unavailable"}
                                </p>
                                {msg.acknowledged_at && (
                                    <p className="text-sm text-green-600">
                                        Acknowledged on {msg.acknowledged_at}{" "}
                                        {msg.acknowledged_roles?.length > 0 && (
                                            <span className="text-gray-700">
                                                (as {msg.acknowledged_roles.join(", ")})
                                            </span>
                                        )}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="flex justify-between mt-4">
                        <button
                            onClick={handlePrev}
                            disabled={page === 1}
                            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="self-center">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={handleNext}
                            disabled={page === totalPages}
                            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
